/**
 * API printable_specs V4 (bascule Gelato 2026-05-17).
 *
 * Modèle générique : `(provider_slug, sku productUid, attributes JSON,
 * label_override?)`. Le BE :
 *   1. ensure le SKU productUid en cache via `ensure_sku_in_cache`
 *   2. calcule `attributes_signature` (SHA256 canonical)
 *   3. INSERT spec parent + 1 variant enfant initial (taille de ce SKU)
 *
 * Le caller peut ajouter d'autres variants (autres tailles) via
 * `addVariantForSize(specId, productUid)`.
 *
 * Routes consommées :
 *   GET    /api/printable-specs                  → list paginé
 *   GET    /api/printable-specs/picker           → flat list active
 *   GET    /api/printable-specs/{id}             → détail
 *   POST   /api/printable-specs                  → create générique
 *   POST   /api/printable-specs/{id}/variants    → add variant (autre taille)
 *   PATCH  /api/printable-specs/{id}             → update attributes/label
 *   POST   /api/printable-specs/{id}/archive     → soft-delete (204)
 *   DELETE /api/printable-specs/{id}             → hard delete (204)
 */

import { apiClient } from './client'
import type { components } from '@/types/api-generated'

export type PrintableSpecResponse = components['schemas']['PrintableSpecResponse']
export type PrintableSpecListResponse = components['schemas']['PrintableSpecListResponse']
export type PrintableSpecCreateRequest = components['schemas']['PrintableSpecCreateRequest']
export type PrintableSpecUpdateRequest = components['schemas']['PrintableSpecUpdateRequest']
export type AddVariantRequest = components['schemas']['AddVariantRequest']

export interface ListPrintableSpecsParams {
  provider_slug?: string
  q?: string
  page?: number
  page_size?: number
}

export const printableSpecsApi = {
  list: async (params: ListPrintableSpecsParams = {}): Promise<PrintableSpecListResponse> => {
    const { data } = await apiClient.get<PrintableSpecListResponse>('/printable-specs', { params })
    return data
  },

  picker: async (provider_slug = 'gelato'): Promise<PrintableSpecResponse[]> => {
    const { data } = await apiClient.get<PrintableSpecResponse[]>('/printable-specs/picker', {
      params: { provider_slug },
    })
    return data
  },

  get: async (id: number): Promise<PrintableSpecResponse> => {
    const { data } = await apiClient.get<PrintableSpecResponse>(`/printable-specs/${id}`)
    return data
  },

  create: async (payload: PrintableSpecCreateRequest): Promise<PrintableSpecResponse> => {
    const { data } = await apiClient.post<PrintableSpecResponse>('/printable-specs', payload)
    return data
  },

  addVariant: async (
    specId: number,
    payload: AddVariantRequest,
  ): Promise<PrintableSpecResponse> => {
    const { data } = await apiClient.post<PrintableSpecResponse>(
      `/printable-specs/${specId}/variants`,
      payload,
    )
    return data
  },

  update: async (
    specId: number,
    payload: PrintableSpecUpdateRequest,
  ): Promise<PrintableSpecResponse> => {
    const { data } = await apiClient.patch<PrintableSpecResponse>(
      `/printable-specs/${specId}`,
      payload,
    )
    return data
  },

  archive: async (id: number): Promise<void> => {
    await apiClient.post(`/printable-specs/${id}/archive`)
  },

  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/printable-specs/${id}`)
  },
}
